import React, { useState } from 'react'
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  TextField,
  LinearProgress
} from '@mui/material'
import {
  Headphones as ListeningIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'

const listeningLevels = [
  { level: 'A1', title: 'Beginner', description: 'Từ vựng đơn giản, tốc độ chậm' },
  { level: 'A2', title: 'Elementary', description: 'Hội thoại ngắn, chủ đề quen thuộc' },
  { level: 'B1', title: 'Intermediate', description: 'Nội dung thường gặp, tốc độ bình thường' },
  { level: 'B2', title: 'Upper-Intermediate', description: 'Chủ đề đa dạng, tốc độ nhanh' },
  { level: 'C1', title: 'Advanced', description: 'Nội dung phức tạp, giọng địa phương' }
]

function Listening() {
  const [selectedLevel, setSelectedLevel] = useState('B1')
  const [listeningTest, setListeningTest] = useState(null)
  const [userAnswers, setUserAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [score, setScore] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentAudio, setCurrentAudio] = useState(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')

  const handleStreamingData = (data) => {
    if (data.type === 'start') {
      setStreamingContent('')
    } else if (data.type === 'chunk') {
      setStreamingContent(prev => prev + data.content)
    } else if (data.type === 'complete') {
      // Use a callback to ensure we get the latest streamingContent
      setStreamingContent(currentContent => {
        try {
          // Try to parse the complete content as JSON
          const jsonMatch = currentContent.match(/```(?:json)?\s*([\s\S]*?)\s*```/)
          if (jsonMatch) {
            const testData = JSON.parse(jsonMatch[1].trim())
            setListeningTest({
              ...testData,
              level: selectedLevel,
              timestamp: new Date().toISOString()
            })
            toast.success('Đã tạo bài test nghe!')
          } else {
            // Fallback: use the raw content
            setListeningTest({
              audioText: currentContent,
              questions: [],
              level: selectedLevel,
              timestamp: new Date().toISOString()
            })
            toast.success('Đã tạo bài test nghe!')
          }
        } catch (error) {
          console.error('Error parsing streaming content:', error)
          console.log('Current content:', currentContent) // Debug log
          toast.error('Có lỗi xảy ra khi xử lý dữ liệu')
        }
        
        // Clear the content after processing
        setIsStreaming(false)
        return '' // Clear the streaming content
      })
    } else if (data.type === 'error') {
      toast.error('Có lỗi xảy ra: ' + data.error)
      setIsStreaming(false)
      setStreamingContent('')
    }
  }

  const handleGenerateTest = async () => {
    setIsLoading(true)
    setIsStreaming(true)
    setListeningTest(null)
    setUserAnswers({})
    setShowResults(false)
    setScore(null)
    setStreamingContent('')

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ASSESSMENT.LISTENING_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          level: selectedLevel
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              handleStreamingData(data)
            } catch (error) {
              console.error('Error parsing SSE data:', error)
            }
          }
        }
      }
    } catch (error) {
      console.error('Listening test generation error:', error)
      toast.error('Có lỗi xảy ra khi tạo bài test')
      setIsStreaming(false)
      setStreamingContent('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAnswerChange = (questionIndex, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }))
  }

  const handleSubmitTest = () => {
    if (!listeningTest || !listeningTest.questions) return

    let correctAnswers = 0
    const totalQuestions = listeningTest.questions.length

    listeningTest.questions.forEach((question, index) => {
      const userAnswer = userAnswers[index]?.toLowerCase().trim()
      const correctAnswer = question.correctAnswer?.toLowerCase().trim()
      
      if (userAnswer === correctAnswer) {
        correctAnswers++
      }
    })

    const finalScore = Math.round((correctAnswers / totalQuestions) * 100)
    setScore({ correct: correctAnswers, total: totalQuestions, percentage: finalScore })
    setShowResults(true)
    
    if (finalScore >= 80) {
      toast.success(`Xuất sắc! Bạn đạt ${finalScore}%`)
    } else if (finalScore >= 60) {
      toast.success(`Tốt! Bạn đạt ${finalScore}%`)
    } else {
      toast.error(`Cần cải thiện! Bạn đạt ${finalScore}%`)
    }
  }

  const playAudio = (text) => {
    if (currentAudio) {
      currentAudio.cancel()
    }

    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'en-US'
      utterance.rate = selectedLevel === 'A1' || selectedLevel === 'A2' ? 0.7 : 0.9
      
      utterance.onstart = () => setIsPlaying(true)
      utterance.onend = () => setIsPlaying(false)
      utterance.onerror = () => {
        setIsPlaying(false)
        toast.error('Lỗi phát âm thanh')
      }
      
      setCurrentAudio(utterance)
      speechSynthesis.speak(utterance)
    } else {
      toast.error('Trình duyệt không hỗ trợ text-to-speech')
    }
  }

  const stopAudio = () => {
    if (currentAudio) {
      speechSynthesis.cancel()
      setIsPlaying(false)
    }
  }

  const allQuestionsAnswered = listeningTest?.questions?.every((_, index) => 
    userAnswers[index]?.trim()
  )

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ListeningIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Luyện nghe
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Cải thiện kỹ năng nghe tiếng Anh qua các bài test theo trình độ
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Level Selection */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Chọn trình độ
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {listeningLevels.map((levelInfo) => (
                  <Card 
                    key={levelInfo.level}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedLevel === levelInfo.level ? 2 : 1,
                      borderColor: selectedLevel === levelInfo.level ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => setSelectedLevel(levelInfo.level)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip label={levelInfo.level} size="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {levelInfo.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {levelInfo.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateTest}
                disabled={isLoading || isStreaming}
                startIcon={<QuizIcon />}
                sx={{ mt: 2 }}
              >
                {isStreaming ? '🔄 Đang tạo bài test...' : isLoading ? 'Đang tạo bài test...' : 'Tạo bài test mới'}
              </Button>
            </Paper>

            {score && (
              <Paper elevation={3} sx={{ p: 3, bgcolor: score.percentage >= 60 ? 'success.50' : 'error.50' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Kết quả
                </Typography>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                  {score.percentage}%
                </Typography>
                <Typography variant="body2">
                  {score.correct}/{score.total} câu đúng
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Listening Test */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, minHeight: '70vh' }}>
              {isLoading && <LinearProgress sx={{ mb: 2 }} />}
              
              {/* Streaming Content Display */}
              {isStreaming && streamingContent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Alert severity="info" sx={{ mb: 3 }}>
                    🔄 Đang tạo bài test nghe...
                  </Alert>
                  <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                      {streamingContent}
                    </Typography>
                  </Paper>
                </motion.div>
              )}
              
              {listeningTest && !isStreaming ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Audio Control */}
                  <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Audio
                    </Typography>
                    <Paper elevation={1} sx={{ p: 3, bgcolor: 'primary.50', display: 'inline-block' }}>
                      <VolumeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Box>
                        <Button
                          variant="contained"
                          onClick={() => isPlaying ? stopAudio() : playAudio(listeningTest.audioText)}
                          startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                          sx={{ mr: 1 }}
                        >
                          {isPlaying ? 'Dừng' : 'Phát'}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Bạn có thể nghe nhiều lần
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Questions */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Câu hỏi ({listeningTest.questions?.length || 0} câu)
                    </Typography>
                    {listeningTest.questions?.map((question, index) => (
                      <Card key={index} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            {index + 1}. {question.question}
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder="Nhập câu trả lời..."
                            value={userAnswers[index] || ''}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                            disabled={showResults}
                            sx={{ mb: 2 }}
                          />
                          {showResults && (
                            <Box sx={{ mt: 2 }}>
                              <Alert 
                                severity={
                                  userAnswers[index]?.toLowerCase().trim() === question.correctAnswer?.toLowerCase().trim() 
                                    ? 'success' : 'error'
                                }
                              >
                                <Typography variant="body2">
                                  <strong>Đáp án của bạn:</strong> {userAnswers[index] || '(Không trả lời)'}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Đáp án đúng:</strong> {question.correctAnswer}
                                </Typography>
                              </Alert>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Submit Button */}
                  {!showResults && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmitTest}
                        disabled={!allQuestionsAnswered}
                        startIcon={<CheckIcon />}
                      >
                        Nộp bài
                      </Button>
                    </Box>
                  )}

                  {showResults && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={handleGenerateTest}
                        startIcon={<RefreshIcon />}
                      >
                        Làm bài test mới
                      </Button>
                    </Box>
                  )}
                </motion.div>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ListeningIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Chọn trình độ và tạo bài test nghe
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    AI sẽ tạo audio và câu hỏi phù hợp với trình độ của bạn
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Tips */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, bgcolor: 'info.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'info.main' }}>
            🎧 Mẹo luyện nghe hiệu quả
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Nghe nhiều lần để quen với giọng điệu</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Tập trung vào từ khóa quan trọng</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Đoán nghĩa từ ngữ cảnh</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Luyện tập thường xuyên mỗi ngày</Typography>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Container>
  )
}

export default Listening